import { routeConstants } from 'app/routes/route-constants';
import { CaseState } from 'app/area/case/types-store';
import { ConnectedComponentProps } from 'app/infrastructure/ui/types-uischema';
import get from 'lodash/get';
import { sectionId, tableName } from 'app/area/case/case-data-model';
import { routeFromStoreItemPath } from 'app/infrastructure/store/entity-route';
import { RenderMode } from 'app/types-shared';
import { getById } from 'app/infrastructure/store/query';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';
import { isMyCaseDrafts, inStartupDashboard, shouldNavigateToModal } from 'app/components/helpers';
import { isAvailableSection } from 'app/area/case/selectors/active-section';

export interface UrlOptions{
  withBaseUrl: boolean;
  openNew?: boolean;
}

const buildCaseUrl = (
  caseNumber: number,
  favouriteId?: number,
  dashboardType?: number,
  bowtieCaseId?: number
) => {
  if (shouldNavigateToModal(!!favouriteId, !!dashboardType, !!bowtieCaseId)) {
     if (bowtieCaseId) {
      return routeConstants.bowtieViewOpenCaseFor(bowtieCaseId, caseNumber);
    }
    if (dashboardType) {
      return routeConstants.dashboardWithFavouriteOpenCaseFor(dashboardType, favouriteId, caseNumber);
    }
    if (inStartupDashboard()) {
      return routeConstants.startupDashboardOpenCaseFor(caseNumber);
    }
    if (isMyCaseDrafts()) {
      return routeConstants.myCaseDraftsViewOpenCaseFor(caseNumber);
    }
    return routeConstants.favouriteViewOpenCaseFor(favouriteId, caseNumber);
  }
  
  return routeConstants.caseViewFor(caseNumber);
};

/**
 * Injects a valid URL for the current connected case
 *
 *  @param config - `withBaseUrl:true` will include the virtual directory name when
 * hosted outside root.
 * @param state
 * @param uiEngineProps
 */
export const urlToConnectedCase = (config:UrlOptions = {withBaseUrl:false}) => (
  state: CaseState,
  uiEngineProps: ConnectedComponentProps
) => {
  const componentEntityType =
    uiEngineProps.componentDescriptor.entityTypePath &&
    uiEngineProps.componentDescriptor.entityTypePath.slice(-1)[0];
  
  if (componentEntityType && componentEntityType !== tableName.connectedCases && componentEntityType !== tableName.connectedActionView) {
    return undefined;
  }

  if (!state.entities) {
    return undefined;
  }

  const casePk = uiEngineProps.hostProps().entityId;
  const idPath = uiEngineProps.resolveContext.path;
  
  // Workaround for connected cases in case listing
  const indexKey = state.entities[casePk] ? casePk : Object.keys(state.entities).find(key => Number(key.split('-')[0]) === casePk);
  
  const connectedCase = getById(
    state.editing ? state.editing.values : state.entities[indexKey],
    idPath
  );
  const caseNumber = Number(get(connectedCase, 'ConnectedCaseNo[0].value'));
  const favouriteId  = uiEngineProps.params?.favouriteId;
  const dashboardType = uiEngineProps.params?.dashboardType;
  const bowtieCaseId = uiEngineProps.params?.bowtieCaseId;
  let url = buildCaseUrl(caseNumber, favouriteId, dashboardType, bowtieCaseId);
  if(config.withBaseUrl && url){
    url = toAbsoluteUrl(url);
  }
  return {url, urlOpenNew: config.openNew};
};
/**
 * Injects a URL for the current case, regardless of read or edit mode.
 *
 * A configured `linkDomain` (section) that is not available to the user, e.g. a basic user who cannot see
 * all tabs, is cleared so that the link neither shows nor navigates to a `section` parameter.
 *
 * @param state
 * @param uiEngineProps
 */
export const urlToCase = (config: UrlOptions = { withBaseUrl: false }) => (
  state: CaseState,
  uiEngineProps: ConnectedComponentProps
) => {
  if (uiEngineProps.url) {
    return undefined;
  }
  const caseNumber = uiEngineProps.hostProps().caseNumber;
  const favouriteId  = uiEngineProps.params?.favouriteId;
  const dashboardType = uiEngineProps.params?.dashboardType;
  const bowtieCaseId = uiEngineProps.params?.bowtieCaseId;
  let url = buildCaseUrl(caseNumber, favouriteId, dashboardType, bowtieCaseId);
  if (config.withBaseUrl) {
    url = toAbsoluteUrl(url);
  }
  const linkDomain = Number(uiEngineProps.componentDescriptor?.properties?.linkDomain);
  const unavailableLinkDomain = !!linkDomain && !isAvailableSection(state, uiEngineProps, linkDomain);
  return unavailableLinkDomain ?
    { url, urlOpenNew: config.openNew, linkDomain: undefined } :
    { url, urlOpenNew: config.openNew };
};

const findByAttribute = (path: any[], attributeName: string, attributeValue: number | string) => {
  for (const node of path) {
    if (node[attributeName] === attributeValue) {
      return node.domainId;
    }
    if (node.children) {
      const result = findByAttribute(node.children, attributeName, attributeValue);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

/**
 * Injects a deep link url for the entity specified by resolveContext and hostProps().caseNumber
 *
 * @param state
 * @param uiEngineProps
 */
export const urlToChildEntity =  (toSection: boolean = false) => (
  state: any,
  uiEngineProps: ConnectedComponentProps  
) => {
  
  const path = [...uiEngineProps.resolveContext.path];
  const componentEntityType =
    uiEngineProps.componentDescriptor?.entityTypePath &&
    uiEngineProps.componentDescriptor?.entityTypePath.slice(-1)[0];
  
  if (!path || !path.length) {
    return undefined;
  }

  if (componentEntityType && componentEntityType === tableName.connectedCases) {
    return undefined;
  }
  
  const caseItem = { ...path[0] };
  caseItem.tblTrans = uiEngineProps.hostProps().caseNumber;
  path[0] = caseItem;
  //enable deep linking to section if needed e.g., in actions
  const tableItem = toSection && path[1] &&  Object.keys(path[1]).toString();
  const uiDefinition = state?.uiDefinitions?.[0]?.definition?.rootDomain?.children;
  const targetSection = sectionId[tableItem]?.section;
  const sectionDomainExists = uiDefinition && findByAttribute(uiDefinition, 'domainId', targetSection);
  const section = !!tableItem && !!sectionDomainExists && targetSection;
  
  const params = uiEngineProps.params || {};
  const casePathPrefix = inStartupDashboard() ? routeConstants.startupDashboard : document.location.pathname.split('/case')?.[0];
  const appendCasePathPrefix = shouldNavigateToModal(params.favouriteId, params.dashboardType, params.bowtie) && !!casePathPrefix.length;
  const parentRoute = appendCasePathPrefix ? casePathPrefix : '';
  
  return { url: `${parentRoute}${routeFromStoreItemPath(path, RenderMode.read)}${!!section ? ('?section=' + section) : ''}` };
};

export const caseTypeLinkDomain = (state: CaseState, uiEngineProps: ConnectedComponentProps) => {
  const caseTypeLinkDomains = uiEngineProps.componentDescriptor.properties.caseTypeLinkDomains as string;
  const casePk = uiEngineProps.hostProps().entityId;
  const idPath = uiEngineProps.resolveContext.path;
  const indexKey = state.entities[casePk] ? casePk : Object.keys(state.entities).find(key => Number(key.split('-')[0]) === casePk);

  const connectedCase = getById(
    state.editing ? state.editing.values : state.entities[indexKey],
    idPath
  );
  const connectedCaseType = get(connectedCase, 'ConnectedCaseType[0].value') as string;
  try {
    const parsedCaseTypeLinkDomains = caseTypeLinkDomains.replace(/(\d+):/g, '"$1":');
    const linkDomains = JSON.parse(parsedCaseTypeLinkDomains);
    return linkDomains[connectedCaseType] ? { linkDomain: linkDomains[connectedCaseType] } : null;
  } catch (error) {
    return null;
  }
};

export const caseTypeLinkDashboardId = (state: CaseState, uiEngineProps: ConnectedComponentProps) => {
  const caseTypeLinkDashboardId = uiEngineProps.componentDescriptor.properties.caseTypeLinkDashboardId as string;
  const casePk = uiEngineProps.hostProps().entityId;
  const idPath = uiEngineProps.resolveContext.path;
  const indexKey = state.entities[casePk] ? casePk : Object.keys(state.entities).find(key => Number(key.split('-')[0]) === casePk);

  const connectedCase = getById(
    state.editing ? state.editing.values : state.entities[indexKey],
    idPath
  );
  const connectedCaseType = get(connectedCase, 'ConnectedCaseType[0].value') as string;
  try {
    const parsedCaseTypeLinkDashboardId = caseTypeLinkDashboardId.replace(/(\d+):/g, '"$1":');
    const dashboardId = JSON.parse(parsedCaseTypeLinkDashboardId);
    return dashboardId[connectedCaseType] ? { dashboardId: dashboardId[connectedCaseType] } : null;
  } catch (error) {
    return null;
  }
};
